import type { JSX } from 'react';

import { Permission } from '@bookora/shared';
import type { RouteObject } from 'react-router-dom';
import { Navigate, useParams } from 'react-router-dom';

import { RequirePermission } from '@/app/require-permission';
import { RouteErrorBoundary } from '@/components/route-error-boundary';
import { RequirePlanFeature } from '@/features/plan/require-plan-feature';

/**
 * Child routes contributed by the inventory feature.
 *
 * `/inventory` itself is a NAVIGATION destination from `app/sections.ts`. The
 * extra paths below are deep links other parts of the product hand out, and
 * none of them is a screen of its own: each redirects onto the inventory page
 * with the query parameters that open exactly the thing the link was about
 * (see the table in `inventory-page.tsx`). Redirecting to the bare page used to
 * be enough to avoid «صفحه پیدا نشد», and not enough to be useful: the id the
 * link carried was dropped, and the user had to hunt through 105 batches for
 * the one the notification named.
 *
 *  - `/inventory/receive` — the onboarding checklist's «انجامش بده»
 *    (`clinic-onboarding.service.ts`, step `inventory`). Opens the receive
 *    drawer, which is what the step is about.
 *  - `/inventory/stock/:materialId` — the sweep's «کمبود موجودی» bell
 *    notification (`InventoryAlertsService.notify`). Opens that material.
 *  - `/inventory/lots/:lotId` — its «انقضای نزدیک» notification. Opens the
 *    batches tab narrowed to that batch.
 */
function ToMaterial(): JSX.Element {
  const { materialId = '' } = useParams();
  return (
    <Navigate to={`/inventory?tab=stock&material=${encodeURIComponent(materialId)}`} replace />
  );
}

function ToLot(): JSX.Element {
  const { lotId = '' } = useParams();
  return (
    <Navigate to={`/inventory?tab=stock&view=lots&lot=${encodeURIComponent(lotId)}`} replace />
  );
}

export const routes: RouteObject[] = [
  {
    path: 'inventory/receive',
    element: (
      <RequirePermission anyOf={[Permission.INVENTORY_VIEW]}>
        <Navigate to="/inventory?receive=1" replace />
      </RequirePermission>
    ),
    // A throw with no boundary of its own bubbles to Root and replaces the
    // whole application rather than the screen that broke.
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'inventory/stock/:materialId',
    element: (
      <RequirePermission anyOf={[Permission.INVENTORY_VIEW]}>
        <ToMaterial />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'inventory/lots/:lotId',
    element: (
      <RequirePermission anyOf={[Permission.INVENTORY_VIEW]}>
        {/* A batch is lot tracking (`lotInventory`). Without it the link lands
            on the plan-lock state; after a downgrade (read-only) the batch
            the link names is still opened — data stays readable. */}
        <RequirePlanFeature feature="lotInventory" variant="section">
          <ToLot />
        </RequirePlanFeature>
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
];

export default routes;
